import { PriceUtils } from '@wix/bookings-uou-mappers';
import { DynamicPriceInfo } from '@wix/bookings-uou-types';
import {
  ServiceVariant,
  ServiceOptionType,
  ServiceOptionsAndVariants,
} from '@wix/ambassador-bookings-catalog-v1-service-options-and-variants/types';

import { CalendarContext } from '../context/contextFactory';
import { Optional, SelectedVariantOptions } from '../../types/types';
import { CalendarState } from '../../components/BookingCalendar/controller';
import { MultiDynamicPriceInfo } from '@wix/bookings-uou-types/src';
import { SlotAvailability } from '@wix/ambassador-bookings-availability-v1-slot-availability/types';
import type { CalendarServiceSelections } from '@wix/bookings-catalog-calendar-viewer-utils';
import { Service } from '@wix/ambassador-bookings-services-v2-service/build/cjs/types.impl';
import {
  getServiceCurrency,
  getServicePrice,
} from '@wix/bookings-calendar-catalog-viewer-mapper';

export const getDynamicPricingInfo = ({
  selectedSlot,
  servicesSelections,
}: {
  servicesSelections: Omit<CalendarServiceSelections, 'resources'>[];
  selectedSlot: SlotAvailability;
}): MultiDynamicPriceInfo => {
  const { nestedSlots, slot } = selectedSlot;

  if (nestedSlots?.length) {
    return servicesSelections
      .map((selections, index) => ({
        ...selections,
        startDate: nestedSlots[index].start!,
      }))
      .filter((selections) => selections.variants?.length)
      .map(({ id: serviceId, startDate, variants }) => ({
        serviceId,
        startDate,
        dynamicPriceInfo: {
          selectedVariants: variants!,
        },
      }));
  }

  return servicesSelections
    .filter(({ variants }) => variants?.length)
    .map(({ variants, id: serviceId }) => ({
      serviceId,
      startDate: slot?.startDate!,

      dynamicPriceInfo: {
        selectedVariants: variants!,
      },
    }));
};

export const deprecatedGetDynamicPricingInfo = (
  selectedVariantsOptions: SelectedVariantOptions[],
): Optional<DynamicPriceInfo> => {
  if (selectedVariantsOptions.length > 0) {
    return {
      selectedVariants: selectedVariantsOptions.map(
        ({ choices, numberOfParticipants }) => {
          return {
            numberOfParticipants,
            choices,
          };
        },
      ),
    };
  }
};

export const getFormattedPrice = ({
  context,
  state,
  service,
  choiceId,
  optionId,
}: {
  context: CalendarContext;
  state: CalendarState;
  service: Service;
  choiceId: string;
  optionId: string;
}): string => {
  const { businessInfo } = context;
  const { serviceVariantsMap } = state;
  const serviceVariants = serviceVariantsMap[service.id!];
  const regionalSettingsLocale = businessInfo!.regionalSettingsLocale!;
  const variant = getVariantByChoiceIdAndOptionId(
    serviceVariants,
    choiceId,
    optionId,
  );

  if (variant) {
    return PriceUtils.getFormattedCurrency({
      price: Number(variant.price!.value),
      currency: variant.price!.currency!,
      locale: regionalSettingsLocale,
    });
  } else {
    return PriceUtils.getFormattedCurrency({
      price: Number(getServicePrice(service)),
      currency: getServiceCurrency(service)!,
      locale: regionalSettingsLocale,
    });
  }
};

export const getVariantByChoiceIdAndOptionId = (
  serviceVariants: Optional<ServiceOptionsAndVariants>,
  choiceId: string,
  optionId: string,
): Optional<ServiceVariant> => {
  return serviceVariants?.variants?.values?.find(
    ({ choices }) =>
      choices?.[0]?.optionId === optionId &&
      (choices?.[0]?.staffMemberId === choiceId ||
        choices?.[0]?.custom === choiceId),
  );
};

export const isServiceVariantWithStaff = (
  serviceVariants: Optional<ServiceOptionsAndVariants>,
): boolean => {
  return isServiceVariantWithType(
    serviceVariants,
    ServiceOptionType.STAFF_MEMBER,
  );
};

export const isServiceVariantWithCustom = (
  serviceVariants: Optional<ServiceOptionsAndVariants>,
): boolean => {
  return isServiceVariantWithType(serviceVariants, ServiceOptionType.CUSTOM);
};

const isServiceVariantWithType = (
  serviceVariants: Optional<ServiceOptionsAndVariants>,
  serviceOptionType: ServiceOptionType,
): boolean => {
  return !!(
    serviceVariants &&
    serviceVariants.options?.values?.some(
      ({ type }) => type === serviceOptionType,
    )
  );
};
